import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  applyRecountedMlPathRouteIds,
  applyRideMlPathIgnoredByWaypoints,
  castErrorResult,
  clearRideMlPathIgnoreIfNoUserOwnedWaypoints,
  clearRideMlPathIgnoreReason,
  ConfirmResult,
  createErrorResult,
  createRouteRebuildAppliedAt,
  createSuccessResult,
  DirectionEnum,
  errors,
  isRideStationsOrderSame,
  isSoftHardValidationSingleErrorResponse,
  isSoftValidationSingleErrorResponse,
  lastRouteRebuildAtAtom,
  mapHardValidationErrorToResult,
  normalizeRideMetadata,
  RebuildRouteTypeEnum,
  RecountRideStationsCriteriaDto,
  resolveRecountedStationsInfo,
  RideFormFields,
  rideHasUnsavedStationChangeAtom,
  RidesApi,
  ridesDraggingStationsTempIdsAtom,
  rideShouldOptimizeOnRebuild,
  routeHasUserOwnedRoutingWaypoints,
  simpleResultErrorEmptyResponse,
  stripIgnoredRideMlPathWaypoints,
  toDisplayedRebuildRouteType,
  toEffectiveMlRouteIdsFromRideMetadata,
  toEffectiveMlRouteIdsFromRideRecount,
  type DirectionsServiceEnum,
  type IRecountStationsResultDto,
  type IRideDto,
  type IRideMetadata,
  type IRideRebuildRouteResult,
  type SimpleResult,
} from "@ryde/shared";

import { useModalConfirm } from "../../hooks-singletons";
import { useSoftValidationModalConfirm } from "../use-soft-validation-modal-confirms";
import { useRideDtoActions } from "./use-ride-dto-actions";

export const useRebuildRoute = () => {
  const { t } = useTranslation();
  const { confirmSoftValidationAction } = useSoftValidationModalConfirm();
  const { confirmDialogResult } = useModalConfirm();
  const { updateEditingRideMetadataRideDate } = useRideDtoActions();
  const setTempDragStationsIds = useSetRecoilState(
    ridesDraggingStationsTempIdsAtom,
  );
  const setRideHasUnsavedStationChange = useSetRecoilState(
    rideHasUnsavedStationChangeAtom,
  );
  const setLastRouteRebuildAt = useSetRecoilState(lastRouteRebuildAtAtom);
  const formContext = useFormContext();

  const rebuildRideRoute = useEvent(
    async (
      rideDto: Partial<IRideDto>,
      options?: {
        rebuildType?: RebuildRouteTypeEnum;
        avoidTolls?: boolean;
        provider?: DirectionsServiceEnum;
        optimize?: boolean;
        /** Skip accept/decline when a new customized ML path is found (e.g. nested rebuild after decline). */
        skipNewMlPathConfirm?: boolean;
        /** When true, the backend does not use a saved ML path for this rebuild. */
        skipMlPath?: boolean;
      },
    ): Promise<SimpleResult<IRideRebuildRouteResult>> => {
      const stationsInitial =
        (rideDto as IRideDto)?.metadata?.stationsInfo || [];

      if (!stationsInitial?.length) {
        return createErrorResult(errors.ride.noStationToRebuild);
      }

      if (!rideDto?.rideStart) {
        return createErrorResult(errors.ride.noInitialDateInRide);
      }

      if (!rideDto?.id) {
        return createErrorResult(errors.ride.invalidRideId);
      }

      const { rebuildType, avoidTolls, provider, skipMlPath } = options || {};
      const isOptimizedRebuildEnabledForRide =
        rideShouldOptimizeOnRebuild(rideDto);
      const isRebuildTypeRequiredOptimization =
        rebuildType === RebuildRouteTypeEnum.Default ||
        rebuildType === RebuildRouteTypeEnum.RefreshCache ||
        rebuildType === RebuildRouteTypeEnum.BestFit ||
        rebuildType === RebuildRouteTypeEnum.OptimizeStationOrderOsrmFirst ||
        rebuildType === RebuildRouteTypeEnum.ProviderOptimization ||
        rebuildType === RebuildRouteTypeEnum.EndpointsLocked;

      const optimize =
        options?.optimize ??
        Boolean(
          isOptimizedRebuildEnabledForRide && isRebuildTypeRequiredOptimization,
        );

      const {
        id,
        metadata: {
          passengerIds = [],
          passengersInfo = [],
          removedAreaWaypoints,
        } = {},
        rideStart,
        rideEnd,
        direction,
        supplierId,
      } = rideDto;

      const criteriaRecount = new RecountRideStationsCriteriaDto({
        id,
        passengerIds,
        passengersInfo,
        stations: stationsInitial,
        rideStart,
        rideEnd,
        optimize,
        direction,
        supplierId,
        rebuildType,
        avoidTolls,
        provider,
        skipMlPath,
        vehicleTypeId:
          formContext?.getValues(RideFormFields.vehicleTypeId) ??
          rideDto?.vehicleTypeId,
        sourceUsedCachedRouteId: rideDto.metadata?.usedCachedRouteId,
        sourceCustomStationRouteId: rideDto.metadata?.customStationRouteId,
        removedAreaWaypoints,
      });

      let recountedResult = (await RidesApi.recountStationInfo(
        criteriaRecount,
      )) as SimpleResult<IRecountStationsResultDto> | undefined;

      if (isSoftValidationSingleErrorResponse(recountedResult?.data)) {
        const softValidationResult = await confirmSoftValidationAction(
          recountedResult.data,
        );

        if (softValidationResult === ConfirmResult.CANCEL) {
          return createSuccessResult({});
        }

        criteriaRecount.token = recountedResult.data.token;
        recountedResult = (await RidesApi.recountStationInfo(
          criteriaRecount,
        )) as SimpleResult<IRecountStationsResultDto> | undefined;
      } else if (
        isSoftHardValidationSingleErrorResponse(recountedResult?.data)
      ) {
        const hardValidationError = recountedResult.data.validationErrors[0];
        return mapHardValidationErrorToResult<IRideRebuildRouteResult>(
          hardValidationError,
        );
      }

      if (!recountedResult) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (!recountedResult.success) {
        return castErrorResult(recountedResult);
      }

      const recountedRoute = recountedResult.data;

      if (!recountedRoute?.stationsInfo?.length) {
        return createErrorResult(
          errors.ride.stationsNotRecountedSuccessfully,
          "Stations not recounted successfully",
        );
      }

      const previousCustomId = rideDto.metadata?.customStationRouteId ?? null;
      const nextCustomId = recountedRoute.customStationRouteId ?? null;
      const hasFoundNewCustomizedMlPath =
        !options?.skipNewMlPathConfirm &&
        !rideDto.metadata?.ignoreMLPathReason &&
        nextCustomId !== null &&
        nextCustomId !== undefined &&
        nextCustomId !== previousCustomId &&
        routeHasUserOwnedRoutingWaypoints(stationsInitial);

      if (hasFoundNewCustomizedMlPath) {
        const confirmationResult = await confirmDialogResult({
          title: t("ride.SAVE_ROUTE.WAYPOINTS_ML_PATH_CONFIRM_TITLE"),
          message: t("ride.SAVE_ROUTE.WAYPOINTS_ML_PATH_CONFIRM_TEXT"),
        });

        if (confirmationResult !== ConfirmResult.OK) {
          // Decline new ML path — keep all waypoints, live recalc without customized ML.
          const ignoredMetadata = applyRideMlPathIgnoredByWaypoints(
            rideDto.metadata ?? {},
          );
          return rebuildRideRoute(
            { ...rideDto, metadata: ignoredMetadata },
            {
              ...options,
              rebuildType: RebuildRouteTypeEnum.KeepOrder,
              optimize: false,
              skipNewMlPathConfirm: true,
              skipMlPath: true,
            },
          );
        }

        // Accept new ML path — all prior custom/user-owned waypoints leave; new path as-is.
        const acceptedRouteRebuildAt = createRouteRebuildAppliedAt();
        setLastRouteRebuildAt(acceptedRouteRebuildAt);
        const metadataAccepted = clearRideMlPathIgnoreReason({
          ...(rideDto.metadata || {}),
          stationsInfo: resolveRecountedStationsInfo({
            recounted: recountedRoute.stationsInfo,
            source: stationsInitial,
            sourceRouteIds: toEffectiveMlRouteIdsFromRideMetadata(
              rideDto.metadata,
            ),
            recountRouteIds:
              toEffectiveMlRouteIdsFromRideRecount(recountedRoute),
            shouldAcceptNewCustomizedMlPath: true,
          }),
          routePolyline: recountedRoute.routePolyline,
          totalDuration: recountedRoute.totalDuration,
          totalDistance: recountedRoute.totalDistance,
          distances: recountedRoute.distances,
          durations: recountedRoute.durations,
          customStationRouteId: nextCustomId,
          usedCachedRouteId: recountedRoute.usedCachedRouteId ?? null,
          directionsService: recountedRoute.directionsService,
          routeRebuildAt: acceptedRouteRebuildAt,
          routeRebuildSource: recountedRoute.routeRebuildSource,
          rebuildRouteType: toDisplayedRebuildRouteType(options?.rebuildType),
        });

        const acceptedMetadataNormalized = normalizeRideMetadata(
          metadataAccepted,
          {
            direction: rideDto?.direction || DirectionEnum.PickUp,
          },
        );
        updateEditingRideMetadataRideDate({
          rideId: rideDto.id,
          updatedMetadata: acceptedMetadataNormalized,
        });
        setTempDragStationsIds(undefined);

        return createSuccessResult({
          routeRebuildSource: recountedRoute.routeRebuildSource,
          directionsService: recountedRoute.directionsService,
        });
      }

      const resolvedStationsInfo = resolveRecountedStationsInfo({
        recounted: recountedRoute.stationsInfo,
        source: stationsInitial,
        sourceRouteIds: toEffectiveMlRouteIdsFromRideMetadata(rideDto.metadata),
        recountRouteIds: toEffectiveMlRouteIdsFromRideRecount(recountedRoute),
      });
      const metadataCloned: IRideMetadata = stripIgnoredRideMlPathWaypoints({
        ...clearRideMlPathIgnoreIfNoUserOwnedWaypoints({
          metadata: { ...(rideDto?.metadata || {}) },
          stations: resolvedStationsInfo,
        }),
        stationsInfo: resolvedStationsInfo,
      });

      const doesResolvedMatchRecountOrder = isRideStationsOrderSame(
        metadataCloned.stationsInfo ?? [],
        recountedRoute.stationsInfo,
      );
      const keepOrderRebuild =
        options?.rebuildType === RebuildRouteTypeEnum.KeepOrder;

      // Geometry must match stations order. If merge kept drag order over a mismatched recount, drop polyline
      // so the map regenerates the path (OSRM) for the UI order.
      if (doesResolvedMatchRecountOrder) {
        metadataCloned.routePolyline = recountedRoute.routePolyline;
        metadataCloned.totalDuration = recountedRoute.totalDuration;
        metadataCloned.totalDistance = recountedRoute.totalDistance;
        metadataCloned.distances = recountedRoute.distances;
        metadataCloned.durations = recountedRoute.durations;
      } else {
        metadataCloned.routePolyline = undefined;
        metadataCloned.distances = undefined;
        metadataCloned.durations = undefined;
      }

      const metadataWithMlPathIds = applyRecountedMlPathRouteIds({
        metadata: metadataCloned,
        customStationRouteId: recountedRoute.customStationRouteId,
        usedCachedRouteId: recountedRoute.usedCachedRouteId,
        shouldOptimize: optimize,
      });
      metadataCloned.customStationRouteId =
        metadataWithMlPathIds.customStationRouteId;
      metadataCloned.usedCachedRouteId =
        metadataWithMlPathIds.usedCachedRouteId;
      const routingEngine = recountedRoute.directionsService ?? provider;
      if (routingEngine) {
        metadataCloned.directionsService = routingEngine;
      }
      const routeRebuildAt = createRouteRebuildAppliedAt();
      metadataCloned.routeRebuildAt = routeRebuildAt;
      metadataCloned.routeRebuildSource = recountedRoute.routeRebuildSource;
      // The backend returns the sent list, without deletes whose area left the ride.
      metadataCloned.removedAreaWaypoints = recountedRoute.removedAreaWaypoints;
      setLastRouteRebuildAt(routeRebuildAt);

      // Update rebuildRouteType based on rebuild type
      if (options?.rebuildType && rideDto.id) {
        metadataCloned.rebuildRouteType = toDisplayedRebuildRouteType(
          options.rebuildType,
        );
      }

      const metadataRecounted = normalizeRideMetadata(metadataCloned, {
        direction: rideDto?.direction || DirectionEnum.PickUp,
        skipReorderStationsByTime: keepOrderRebuild,
      });
      updateEditingRideMetadataRideDate({
        rideId: rideDto.id,
        updatedMetadata: metadataRecounted,
        skipReorderStationsByTime: keepOrderRebuild,
      });
      setTempDragStationsIds(undefined);

      if (keepOrderRebuild && rideDto.id) {
        // Only trust encoded geometry when it matches the UI station order; otherwise keep unsaved so
        // MapLibre drops stale polyline and OSRM-follows the reordered waypoints.
        setRideHasUnsavedStationChange(!doesResolvedMatchRecountOrder);
      }

      return createSuccessResult({
        routeRebuildSource: recountedRoute.routeRebuildSource,
        directionsService: recountedRoute.directionsService ?? provider,
      });
    },
  );

  return { rebuildRideRoute };
};
